import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  UserPlus,
  Shield,
  Activity,
  Search,
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  Crown,
  Edit,
  Trash2,
  Filter,
  ChevronDown,
  Building2,
  Lock,
  Unlock,
  MessageSquare,
  Zap,
  Loader2,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface TeamMember {
  id: number;
  name: string;
  email: string;
  role: string;
  avatar: string;
  status: string;
  last_active: string;
  tasks_completed: number;
  messages_this_week: number;
  joined_at: string;
}

interface ActivityLog {
  id: number;
  user: string;
  action: string;
  target: string;
  time: string;
  type: string;
}

const roles = [
  {
    name: 'Admin',
    description: 'Full access to all features and settings',
    permissions: ['all'],
    color: 'from-purple-500 to-pink-500',
  },
  {
    name: 'Manager',
    description: 'Can manage team members and view analytics',
    permissions: ['manage_team', 'view_analytics', 'create_tasks', 'use_ai'],
    color: 'from-blue-500 to-cyan-500',
  },
  {
    name: 'Member',
    description: 'Can use AI employees and create tasks',
    permissions: ['create_tasks', 'use_ai', 'view_own_analytics'],
    color: 'from-green-500 to-emerald-500',
  },
  {
    name: 'Viewer',
    description: 'Read-only access to dashboards',
    permissions: ['view_dashboards'],
    color: 'from-gray-500 to-gray-600',
  },
];

type TabType = 'members' | 'permissions' | 'activity';

// Format relative time
const formatRelativeTime = (dateString: string) => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays} days ago`;
};

export default function Team() {
  const { token } = useAuthStore();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [activeTab, setActiveTab] = useState<TabType>('members');
  const [searchQuery, setSearchQuery] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState('all');
  const [loading, setLoading] = useState(true);

  // Data states
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);

  // Invite form states
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  const fetchTeamData = async () => {
    if (!token) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };

      const [membersRes, activityRes] = await Promise.all([
        fetch(`${API_URL}/team/members`, { headers }),
        fetch(`${API_URL}/team/activity?limit=10`, { headers }),
      ]);

      if (membersRes.ok) {
        const data = await membersRes.json() as { success: boolean; data: TeamMember[] };
        if (data.success) setTeamMembers(data.data);
      }

      if (activityRes.ok) {
        const data = await activityRes.json() as { success: boolean; data: ActivityLog[] };
        if (data.success) setActivityLogs(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch team data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamData();
  }, [token]);

  const handleInvite = async () => {
    if (!inviteEmail) {
      setInviteError('Email is required');
      return;
    }

    setInviteLoading(true);
    setInviteError('');
    setInviteSuccess('');

    try {
      const response = await fetch(`${API_URL}/team/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: inviteEmail,
          role: inviteRole,
        }),
      });

      const data = await response.json() as { success: boolean; message?: string; error?: string };

      if (data.success) {
        setInviteSuccess('Invitation sent successfully!');
        setInviteEmail('');
        setInviteRole('member');
        // Close modal after 2 seconds
        setTimeout(() => {
          setShowInviteModal(false);
          setInviteSuccess('');
        }, 2000);
      } else {
        setInviteError(data.error || 'Failed to send invitation');
      }
    } catch (error) {
      setInviteError('Failed to send invitation. Please try again.');
    } finally {
      setInviteLoading(false);
    }
  };

  const tabs = [
    { id: 'members' as TabType, label: 'Team Members', icon: Users, count: teamMembers.length },
    { id: 'permissions' as TabType, label: 'Roles & Permissions', icon: Shield },
    { id: 'activity' as TabType, label: 'Activity Log', icon: Activity },
  ];

  const filteredMembers = teamMembers.filter((member) => {
    const matchesSearch =
      member.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = selectedRole === 'all' || member.role?.toLowerCase() === selectedRole.toLowerCase();
    return matchesSearch && matchesRole;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'pending':
        return 'bg-yellow-500';
      case 'inactive':
        return 'bg-gray-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'create':
        return <Zap className="w-4 h-4 text-purple-400" />;
      case 'invite':
        return <UserPlus className="w-4 h-4 text-blue-400" />;
      case 'complete':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'permission':
        return <Shield className="w-4 h-4 text-yellow-400" />;
      case 'ai':
        return <Zap className="w-4 h-4 text-cyan-400" />;
      case 'chat':
        return <MessageSquare className="w-4 h-4 text-pink-400" />;
      default:
        return <Activity className="w-4 h-4 text-gray-400" />;
    }
  };

  // Stats calculations
  const activeCount = teamMembers.filter(m => m.status === 'active').length;
  const totalTasks = teamMembers.reduce((sum, m) => sum + (m.tasks_completed || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Team Management</h1>
          <p className={`mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Manage your team and permissions</p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl text-white font-medium hover:shadow-glow-sm transition-shadow"
        >
          <UserPlus className="w-5 h-5" />
          Invite Member
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: 'Total Members',
            value: loading ? '...' : teamMembers.length.toString(),
            change: 'In your organization',
            icon: Users,
            color: 'from-purple-500 to-pink-500',
          },
          {
            label: 'Active Now',
            value: loading ? '...' : activeCount.toString(),
            change: `${teamMembers.length > 0 ? Math.round((activeCount / teamMembers.length) * 100) : 0}% of team`,
            icon: Activity,
            color: 'from-green-500 to-emerald-500',
          },
          {
            label: 'Tasks Completed',
            value: loading ? '...' : totalTasks.toString(),
            change: 'By all team members',
            icon: Zap,
            color: 'from-yellow-500 to-orange-500',
          },
          {
            label: 'Roles Defined',
            value: '4',
            change: 'Admin, Manager, Member, Viewer',
            icon: Shield,
            color: 'from-blue-500 to-cyan-500',
          },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`p-5 rounded-2xl border backdrop-blur-xl ${isDark ? 'bg-dark-800/80 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{stat.label}</p>
                <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {loading && stat.value === '...' ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    stat.value
                  )}
                </p>
                <p className={`text-xs mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{stat.change}</p>
              </div>
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Tabs */}
      <div className={`flex flex-wrap gap-2 border-b pb-4 ${isDark ? 'border-border' : 'border-slate-200'}`}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-purple-500 to-cyan-500 text-white'
                : isDark ? 'text-text-muted hover:text-white hover:bg-dark-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.count !== undefined && (
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === tab.id ? 'bg-white/20' : isDark ? 'bg-dark-600' : 'bg-slate-200'
              }`}>
                {loading ? '...' : tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'members' && (
          <motion.div
            key="members"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                <input
                  type="text"
                  placeholder="Search members..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-accent-purple ${isDark ? 'bg-dark-800 border-border text-white placeholder-text-muted' : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'}`}
                />
              </div>
              <div className="relative">
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className={`appearance-none pl-4 pr-10 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-accent-purple cursor-pointer ${isDark ? 'bg-dark-800 border-border text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                >
                  <option value="all">All Roles</option>
                  <option value="admin">Admin</option>
                  <option value="manager">Manager</option>
                  <option value="member">Member</option>
                  <option value="viewer">Viewer</option>
                </select>
                <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
              </div>
            </div>

            {/* Members Table */}
            <div className={`overflow-hidden rounded-2xl border ${isDark ? 'bg-dark-800/80 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
              {loading ? (
                <div className="h-64 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
                </div>
              ) : filteredMembers.length === 0 ? (
                <div className={`h-64 flex items-center justify-center ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  <div className="text-center">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No team members found</p>
                    <button
                      onClick={() => setShowInviteModal(true)}
                      className="text-purple-400 text-sm mt-2"
                    >
                      Invite your first team member →
                    </button>
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className={`border-b ${isDark ? 'border-border' : 'border-slate-200'}`}>
                        <th className={`text-left py-4 px-6 text-sm font-medium ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Member</th>
                        <th className={`text-left py-4 px-6 text-sm font-medium ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Role</th>
                        <th className={`text-left py-4 px-6 text-sm font-medium ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Status</th>
                        <th className={`text-left py-4 px-6 text-sm font-medium ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Last Active</th>
                        <th className={`text-left py-4 px-6 text-sm font-medium ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Activity</th>
                        <th className={`text-right py-4 px-6 text-sm font-medium ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMembers.map((member) => (
                        <tr key={member.id} className={`border-b transition-colors ${isDark ? 'border-border/50 hover:bg-dark-700/50' : 'border-slate-100 hover:bg-slate-50'}`}>
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-white font-medium">
                                {member.avatar}
                              </div>
                              <div>
                                <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{member.name}</p>
                                <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{member.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                              member.role === 'admin' ? 'bg-purple-500/20 text-purple-400' :
                              member.role === 'manager' ? 'bg-blue-500/20 text-blue-400' :
                              member.role === 'member' ? 'bg-green-500/20 text-green-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {member.role === 'admin' && <Crown className="w-3 h-3" />}
                              {member.role?.charAt(0).toUpperCase() + member.role?.slice(1)}
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${getStatusColor(member.status)}`} />
                              <span className={`text-sm capitalize ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>{member.status}</span>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <span className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{member.last_active}</span>
                          </td>
                          <td className="py-4 px-6">
                            <div className={`flex items-center gap-4 text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                              <span className="flex items-center gap-1">
                                <Zap className="w-3.5 h-3.5" />
                                {member.tasks_completed || 0}
                              </span>
                              <span className="flex items-center gap-1">
                                <MessageSquare className="w-3.5 h-3.5" />
                                {member.messages_this_week || 0}
                              </span>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center justify-end gap-2">
                              <button className={`p-2 rounded-lg transition-colors ${isDark ? 'text-text-muted hover:text-white hover:bg-dark-600' : 'text-slate-400 hover:text-slate-900 hover:bg-slate-100'}`}>
                                <Edit className="w-4 h-4" />
                              </button>
                              <button className="p-2 text-text-muted hover:text-accent-red hover:bg-accent-red/10 rounded-lg transition-colors">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {activeTab === 'permissions' && (
          <motion.div
            key="permissions"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            <p className={isDark ? 'text-text-muted' : 'text-slate-500'}>Configure role-based access control for your team</p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {roles.map((role) => (
                <div key={role.name} className={`p-6 rounded-2xl border backdrop-blur-xl ${isDark ? 'bg-dark-800/80 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${role.color} flex items-center justify-center`}>
                        <Shield className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{role.name}</h3>
                        <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{role.description}</p>
                      </div>
                    </div>
                    <button className={`p-2 rounded-lg transition-colors ${isDark ? 'text-text-muted hover:text-white hover:bg-dark-600' : 'text-slate-400 hover:text-slate-900 hover:bg-slate-100'}`}>
                      <Edit className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="space-y-2">
                    <p className={`text-sm font-medium mb-3 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>Permissions</p>
                    <div className="flex flex-wrap gap-2">
                      {role.permissions.map((permission) => (
                        <span
                          key={permission}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs ${isDark ? 'bg-dark-700 text-text-secondary' : 'bg-slate-100 text-slate-600'}`}
                        >
                          {permission === 'all' ? (
                            <Unlock className="w-3 h-3 text-green-400" />
                          ) : (
                            <Lock className={`w-3 h-3 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                          )}
                          {permission.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className={`mt-4 pt-4 border-t ${isDark ? 'border-border' : 'border-slate-200'}`}>
                    <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                      {teamMembers.filter((m) => m.role?.toLowerCase() === role.name.toLowerCase()).length} team members with this role
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Permission Matrix */}
            <div className={`p-6 rounded-2xl border backdrop-blur-xl ${isDark ? 'bg-dark-800/80 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Permission Matrix</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${isDark ? 'border-border' : 'border-slate-200'}`}>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Permission</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-purple-400">Admin</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-blue-400">Manager</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-green-400">Member</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-gray-400">Viewer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { name: 'Manage Team', admin: true, manager: true, member: false, viewer: false },
                      { name: 'View Analytics', admin: true, manager: true, member: true, viewer: true },
                      { name: 'Use AI Employees', admin: true, manager: true, member: true, viewer: false },
                      { name: 'Create Tasks', admin: true, manager: true, member: true, viewer: false },
                      { name: 'Manage Billing', admin: true, manager: false, member: false, viewer: false },
                      { name: 'API Access', admin: true, manager: true, member: false, viewer: false },
                      { name: 'Integrations', admin: true, manager: true, member: true, viewer: false },
                    ].map((perm) => (
                      <tr key={perm.name} className={`border-b ${isDark ? 'border-border/50' : 'border-slate-100'}`}>
                        <td className={`py-3 px-4 text-sm ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>{perm.name}</td>
                        <td className="text-center py-3 px-4">
                          {perm.admin ? (
                            <CheckCircle className="w-5 h-5 text-green-400 mx-auto" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-400/50 mx-auto" />
                          )}
                        </td>
                        <td className="text-center py-3 px-4">
                          {perm.manager ? (
                            <CheckCircle className="w-5 h-5 text-green-400 mx-auto" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-400/50 mx-auto" />
                          )}
                        </td>
                        <td className="text-center py-3 px-4">
                          {perm.member ? (
                            <CheckCircle className="w-5 h-5 text-green-400 mx-auto" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-400/50 mx-auto" />
                          )}
                        </td>
                        <td className="text-center py-3 px-4">
                          {perm.viewer ? (
                            <CheckCircle className="w-5 h-5 text-green-400 mx-auto" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-400/50 mx-auto" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'activity' && (
          <motion.div
            key="activity"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            <div className="flex justify-between items-center">
              <p className={isDark ? 'text-text-muted' : 'text-slate-500'}>Recent activity from your team</p>
              <button className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${isDark ? 'bg-dark-700 hover:bg-dark-600 text-white' : 'bg-white border border-slate-200 hover:bg-slate-50 text-slate-700'}`}>
                <Filter className="w-4 h-4" />
                Filter
              </button>
            </div>

            <div className={`rounded-2xl border divide-y ${isDark ? 'bg-dark-800/80 border-white/10 divide-border' : 'bg-white border-slate-200 divide-slate-100 shadow-sm'}`}>
              {loading ? (
                <div className="h-64 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
                </div>
              ) : activityLogs.length === 0 ? (
                <div className={`h-64 flex items-center justify-center ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  <div className="text-center">
                    <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No activity yet</p>
                    <p className="text-sm mt-1">Activity will appear here when team members start using the platform</p>
                  </div>
                </div>
              ) : (
                activityLogs.map((log) => (
                  <div key={log.id} className={`flex items-start gap-4 p-4 transition-colors ${isDark ? 'hover:bg-dark-700/50' : 'hover:bg-slate-50'}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isDark ? 'bg-dark-700' : 'bg-slate-100'}`}>
                      {getActivityIcon(log.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        <span className="font-medium">{log.user}</span>{' '}
                        <span className={isDark ? 'text-text-muted' : 'text-slate-500'}>{log.action}</span>{' '}
                        <span className="font-medium text-accent-purple">{log.target}</span>
                      </p>
                      <p className={`text-xs mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{formatRelativeTime(log.time)}</p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {activityLogs.length > 0 && (
              <div className="flex justify-center">
                <button className={`px-4 py-2 text-sm transition-colors ${isDark ? 'text-text-muted hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                  Load more activity
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Invite Modal */}
      <AnimatePresence>
        {showInviteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => {
              setShowInviteModal(false);
              setInviteError('');
              setInviteSuccess('');
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`w-full max-w-md rounded-2xl border p-6 ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200'}`}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Invite Team Member</h2>

              {inviteSuccess && (
                <div className="mb-4 p-4 bg-green-500/20 border border-green-500/30 rounded-xl text-green-400 text-sm flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  {inviteSuccess}
                </div>
              )}

              {inviteError && (
                <div className="mb-4 p-4 bg-red-500/20 border border-red-500/30 rounded-xl text-red-400 text-sm flex items-center gap-2">
                  <XCircle className="w-5 h-5" />
                  {inviteError}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>Email Address</label>
                  <input
                    type="email"
                    placeholder="colleague@company.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    disabled={inviteLoading}
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:border-accent-purple disabled:opacity-50 ${isDark ? 'bg-dark-700 border-border text-white placeholder-text-muted' : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'}`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>Role</label>
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    disabled={inviteLoading}
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:border-accent-purple disabled:opacity-50 ${isDark ? 'bg-dark-700 border-border text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                  >
                    <option value="member">Member</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </div>

                <div className={`p-4 text-sm rounded-xl ${isDark ? 'bg-dark-700/50 text-text-muted' : 'bg-slate-100 text-slate-500'}`}>
                  <p>An invitation email will be sent to the address above. The invitation will expire in 7 days.</p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteError('');
                    setInviteSuccess('');
                  }}
                  disabled={inviteLoading}
                  className={`flex-1 px-4 py-3 rounded-xl font-medium transition-colors disabled:opacity-50 ${isDark ? 'bg-dark-700 hover:bg-dark-600 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleInvite}
                  disabled={inviteLoading || !inviteEmail}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl text-white font-medium hover:shadow-glow-sm transition-shadow disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {inviteLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Invitation'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
